import { useGetIdentity, useGetOne } from "ra-core";

import type { Sale } from "@/components/atomic-crm/types";

import type { Negocio, RolComercial } from "../dominio/tipos";

/**
 * Quién es el usuario actual (misma consulta que useEsAdmin, comparte caché): administrador o
 * no y su rol comercial. 36: «consultor» (Gabriel) hace consultorías; no recibe leads ni cobra
 * comisión.
 */
export const useMiRol = () => {
  const { identity } = useGetIdentity();
  const { data } = useGetOne<Sale & { rol_comercial?: RolComercial | null }>(
    "sales",
    { id: identity?.id as number },
    { enabled: !!identity?.id, staleTime: 5 * 60 * 1000 },
  );
  const esAdmin = data?.administrator === true;
  const rol = data?.rol_comercial ?? null;
  return {
    id: identity?.id ?? null,
    esAdmin,
    rol,
    /** Consultor sin ser administrador: mueve Ventas (salvo Cobrado) en las fichas que lleva. */
    esConsultor: !esAdmin && rol === "consultor",
    /** Quien vende (todo el que no es administrador ni consultor): ve «Mis comisiones». */
    vende: !!data && !esAdmin && rol !== "consultor",
  };
};

/**
 * ¿Lleva este proyecto como Jan y Robert (mueve todas sus etapas)? Un administrador, siempre; un
 * consultor, en Ventas (36: la base no le deja Cobrado ni Clientes) y solo hasta que la consultoría
 * está hecha (37: después la ve sin tocarla).
 */
export const useGestiona = () => {
  const { esAdmin, esConsultor } = useMiRol();
  return (
    p: Pick<Negocio, "embudo"> &
      Partial<Pick<Negocio, "consultoria_realizada_at">>,
  ) =>
    esAdmin ||
    (esConsultor && p.embudo === "ventas" && !p.consultoria_realizada_at);
};

/**
 * ¿Puede guardar los datos de la ficha? Jan y Robert, siempre; los demás, solo si la ficha es suya
 * (la base no deja guardar a nadie más). El setter tras la consultoría y el consultor la ven en
 * solo lectura (37).
 */
export const usePuedeEditarFicha = () => {
  const { id, esAdmin } = useMiRol();
  return (n: Pick<Negocio, "asignado_a">) =>
    esAdmin || (id != null && String(n.asignado_a ?? "") === String(id));
};
